import React, { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { Calendar, User, CheckCircle, X, Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { scheduleAppointment, getDoctors, getDoctor } from "@/lib/api";
import PatientSearchInput from "@/components/shared/PatientSearchInput";
import type { PatientSearchResult } from "@/hooks/usePatientSearch";
import { Doctor } from "@/types/doctor";
import { useAuth } from "@/context/AuthContext";
import { displayAge } from "@/lib/patientAge";

interface Patient {
  id: string; name: string; age: number; gender: string; phone: string; email?: string; date_of_birth?: string;
}

interface AddAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId?: string;
  patients?: Patient[];
  onAdded?: () => void;
  onPatientSelected?: (patient: PatientSearchResult) => void;
  onCreateNewPatient?: () => void;
  showPatientSelection?: boolean;
  title?: string;
  description?: string;
  initialDate?: string;
  initialTime?: string;
}

interface AppointmentForm {
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  date: string;
  time: string;
  reason: string;
  status?: string;
}

const inputClass = "h-9 w-full px-3 py-1 text-sm border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed";


export default function AddAppointmentModal({
  isOpen, onClose,
  patientId = '', patients = [],
  onAdded, onPatientSelected, onCreateNewPatient,
  showPatientSelection = true,
  title = "New Appointment",
  description = "Schedule a new patient appointment",
  initialDate, initialTime,
}: Readonly<AddAppointmentModalProps>) {
  const { currentUser } = useAuth();

  const [form, setForm] = useState<AppointmentForm>({
    patient_id: patientId, doctor_id: '', clinic_id: '',
    date: format(new Date(), "yyyy-MM-dd"), time: '', reason: ''
  });

  const [loading, setLoading]         = useState(false);
  const [doctors, setDoctors]         = useState<Doctor[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [pickedPatient, setPickedPatient]       = useState<PatientSearchResult | null>(null);
  const [patientSearchText, setPatientSearchText] = useState('');

  const isDoctor = !!currentUser?.doctor_id && !!currentUser?.clinic_id;
  const isStaff  = !isDoctor && !!currentUser?.clinic_id;

  const todayStr  = format(new Date(), "yyyy-MM-dd");
  const nowTimeStr = format(new Date(), "HH:mm");
  const minTime   = form.date === todayStr ? nowTimeStr : undefined;

  const doctorsInClinic = useMemo(
    () => (form.clinic_id ? doctors.filter(d => d.clinic_id === form.clinic_id) : doctors),
    [doctors, form.clinic_id]
  );
  const doctorDropdownDisabled = isStaff && doctorsInClinic.length === 1;

  useEffect(() => {
    if (patientId !== form.patient_id) setForm(prev => ({ ...prev, patient_id: patientId }));
  }, [patientId]);

  useEffect(() => {
    if (!isOpen) {
      setForm({
        patient_id: '', doctor_id: isDoctor ? currentUser?.doctor_id || '' : '',
        clinic_id: (isDoctor || isStaff) ? currentUser?.clinic_id || '' : '',
        date: format(new Date(), "yyyy-MM-dd"), time: "", reason: ""
      });
      setLoading(false); setLoadingData(false);
      setPickedPatient(null); setPatientSearchText('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setForm(prev => ({
        ...prev,
        patient_id: patientId || '',
        doctor_id: isDoctor ? currentUser?.doctor_id || '' : prev.doctor_id,
        clinic_id: (isDoctor || isStaff) ? currentUser?.clinic_id || '' : prev.clinic_id,
        date: initialDate || format(new Date(), "yyyy-MM-dd"),
        time: initialTime || "", reason: ""
      }));
    }
  }, [isOpen, patientId]);

  useEffect(() => {
    if (!isOpen) return;
    if (isDoctor && currentUser?.doctor_id && currentUser?.clinic_id) loadDoctorAndClinicForDoctor();
    else if (isStaff && currentUser?.clinic_id) loadDoctorsAndClinicForStaff();
  }, [isOpen, isDoctor, isStaff, currentUser?.doctor_id, currentUser?.clinic_id]);

  const loadDoctorAndClinicForDoctor = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    setLoadingData(true);
    try {
      const doctorData = await getDoctor(currentUser.doctor_id);
      setDoctors(doctorData ? [doctorData] : []);
      setForm(prev => ({ ...prev, doctor_id: currentUser.doctor_id, clinic_id: currentUser.clinic_id }));
    } catch {
      toast({ title: "Loading Error", description: "Failed to load your clinic info", variant: "destructive" });
    } finally { setLoadingData(false); }
  };

  const loadDoctorsAndClinicForStaff = async () => {
    const clinicId = currentUser?.clinic_id;
    if (!clinicId) return;
    setLoadingData(true);
    try {
      const allDoctors = (await getDoctors() || []).filter((d: Doctor) => d.clinic_id === clinicId);
      setDoctors(allDoctors);
      setForm(prev => {
        const next = { ...prev, clinic_id: clinicId };
        if (allDoctors.length > 0 && !prev.doctor_id) next.doctor_id = allDoctors[0].id;
        return next;
      });
    } catch {
      toast({ title: "Loading Error", description: "Failed to load doctors and clinic", variant: "destructive" });
    } finally { setLoadingData(false); }
  };

  const selectedPatient = useMemo(() => {
    if (pickedPatient) return pickedPatient;
    if (!form.patient_id) return null;
    return patients.find(p => p.id === form.patient_id) || null;
  }, [pickedPatient, patients, form.patient_id]);

  const handleField = (field: keyof AppointmentForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handlePatientDeselect = () => { setForm(prev => ({ ...prev, patient_id: '' })); setPickedPatient(null); setPatientSearchText(''); };
  const handlePatientPicked   = (patient: PatientSearchResult) => {
    setForm(prev => ({ ...prev, patient_id: patient.id }));
    setPickedPatient(patient); setPatientSearchText(patient.name);
    onPatientSelected?.(patient);
  };

  const formValidationErrors = useMemo(() => {
    const errors: string[] = [];
    if (showPatientSelection && !form.patient_id) errors.push('Patient required');
    if (showPatientSelection && form.patient_id && !selectedPatient) errors.push('Patient not found');
    if (!form.doctor_id) errors.push('Doctor required');
    if (!form.clinic_id) errors.push('Clinic required');
    if (!form.date) errors.push('Date required');
    if (!form.time) errors.push('Time required');
    return errors;
  }, [showPatientSelection, form, selectedPatient]);

  const isFormValid = formValidationErrors.length === 0;

  const handleSubmit = async () => {
    if (!isFormValid) { toast({ title: "Validation Error", description: "Please fill in all required fields", variant: "destructive" }); return; }
    setLoading(true);
    try {
      await scheduleAppointment({
        patient_id: form.patient_id, doctor_id: form.doctor_id, clinic_id: form.clinic_id,
        date: form.date, time: form.time, reason: (form.reason || '').trim(), status: 'scheduled'
      });
      toast({ title: "Appointment Scheduled", description: "Appointment successfully scheduled" });
      onAdded?.();
    } catch (err: any) {
      toast({ title: "Scheduling Failed", description: err.message || "Failed to schedule", variant: "destructive" });
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden gap-0"
        onPointerDownOutside={e => {
          if ((e.target as HTMLElement)?.closest('[data-patient-search-dropdown]')) e.preventDefault();
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <DialogHeader className="px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-3.5 h-3.5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-sm font-semibold text-foreground">{title}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">{description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        {loadingData ? (
          <div className="px-5 py-8 flex items-center justify-center gap-2.5">
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">Loading…</p>
          </div>
        ) : (
          <form onSubmit={e => { e.preventDefault(); handleSubmit(); }} className="px-5 py-4 space-y-4">

            {/* Patient */}
            {showPatientSelection && (
              selectedPatient ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/20 rounded-lg">
                  <CheckCircle className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground capitalize truncate block">{selectedPatient.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {[displayAge(selectedPatient.age, selectedPatient.date_of_birth), selectedPatient.gender, selectedPatient.phone].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  <button type="button" onClick={handlePatientDeselect} disabled={loading}
                    className="p-1 rounded hover:bg-muted text-muted-foreground flex-shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : form.patient_id ? (
                <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive font-medium">Patient not found</p>
                  <button type="button" onClick={handlePatientDeselect} className="text-xs text-destructive hover:underline mt-1">Clear</button>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Patient <span className="text-destructive">*</span></label>
                  <PatientSearchInput
                    value={patientSearchText} onChange={setPatientSearchText}
                    onSelect={handlePatientPicked}
                    allowCreateNew={!!onCreateNewPatient} onCreateNew={() => onCreateNewPatient?.()}
                    placeholder="Search by name, phone, or MRN…"
                    inputSize="sm" className="h-9"
                  />
                </div>
              )
            )}

            {/* Doctor — staff only */}
            {!isDoctor && (
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Doctor <span className="text-destructive">*</span></label>
                <select
                  value={form.doctor_id} onChange={handleField('doctor_id')}
                  disabled={Boolean(loading || loadingData || doctorDropdownDisabled)}
                  className={inputClass} required
                >
                  <option value="">Select a doctor</option>
                  {(doctorsInClinic.length ? doctorsInClinic : doctors).map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name}{d.specialty ? ` — ${d.specialty}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Date + Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  <Calendar className="w-3 h-3 inline mr-1 text-muted-foreground" />Date <span className="text-destructive">*</span>
                </label>
                <Input
                  type="date" value={form.date} onChange={handleField('date')}
                  disabled={loading} min={todayStr} className="h-9 text-sm" required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  <Clock className="w-3 h-3 inline mr-1 text-muted-foreground" />Time <span className="text-destructive">*</span>
                </label>
                <input
                  type="time" value={form.time}
                  onChange={e => setForm(prev => ({ ...prev, time: e.target.value }))}
                  disabled={loading} min={minTime}
                  className={inputClass} required
                />
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Reason <span className="text-xs text-muted-foreground font-normal">(optional)</span>
              </label>
              <Textarea
                value={form.reason} onChange={handleField('reason')}
                placeholder="e.g. Follow-up, Routine checkup, Fever…"
                disabled={loading} rows={2} className="resize-none text-sm"
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={loading || !isFormValid || loadingData} className="gap-1.5">
                {loading
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scheduling…</>
                  : <><Calendar className="w-3.5 h-3.5" /> Schedule</>
                }
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
